import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  IconButton,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUsers, FiRefreshCw, FiFileText, FiMapPin } from 'react-icons/fi';
import { getAccessLogs, getStudentsCache } from 'services/qrService';
import { getSurveyResponse } from 'services/surveyService';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

// A diferencia de PedagogicalExitsTab.js/SickExitsTab.js, Retiro Anticipado
// no tiene tabla propia (no se "programa" de antemano) — cada retiro es
// simplemente un access_log con exit_reason='pickup', así que este tab es
// de solo lectura sobre /qr/logs filtrado, sin crear/editar/eliminar.
const PickupExitsTab = () => {
  const [logs, setLogs] = useState([]);
  const [namesByRut, setNamesByRut] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFichaOpen, setIsFichaOpen] = useState(false);
  const [fichaData, setFichaData] = useState(null);

  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccessLogs({ exit_reason: 'pickup', per_page: 100 });
      setLogs(res.data ?? []);
    } catch {
      setError('No se pudo cargar la lista de retiros anticipados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    getStudentsCache(1, 200)
      .then(res => {
        const map = {};
        (res.data ?? []).forEach(s => { map[s.rut] = s.nombre; });
        setNamesByRut(map);
      })
      .catch(() => {});
  }, []);

  const handleOpenFicha = (responseId) => {
    setFichaData(null);
    setIsFichaOpen(true);
    getSurveyResponse(responseId)
      .then(setFichaData)
      .catch(() => setFichaData({ error: 'No se pudieron cargar los datos de la ficha' }));
  };

  return (
    <Box fontFamily="jakarta">
      <Flex justify="space-between" align="center" mb={4} gap="10px" flexWrap="wrap">
        <Box>
          <Text fontSize="18px" fontWeight="800" color="ink.900" letterSpacing="-0.02em">Retiros Anticipados</Text>
          <Text fontSize="13px" color="ink.400" fontWeight="500" mt="2px">
            {logs.length} retiro{logs.length !== 1 ? 's' : ''} registrado{logs.length !== 1 ? 's' : ''}
          </Text>
        </Box>
        <Tooltip label="Refrescar">
          <IconButton
            icon={<FiRefreshCw />}
            h="38px" w="38px" borderRadius="11px"
            border="1.5px solid" borderColor="cream.line2"
            bg="white" color="ink.700"
            _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
            onClick={fetchLogs} isLoading={loading} aria-label="Refrescar"
          />
        </Tooltip>
      </Flex>

      <Box bg="white" borderRadius="22px" overflow="hidden" border="1px solid" borderColor="cream.line"
        boxShadow="0 2px 12px -4px rgba(35,30,26,.08)">
        {error && (
          <Alert status="error" m={4} borderRadius="10px">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <VStack py={12} spacing={3}>
            <Spinner size="lg" color="primary.500" />
            <Text fontSize="sm" color={mutedColor}>Cargando retiros anticipados…</Text>
          </VStack>
        ) : logs.length === 0 ? (
          <VStack py={12} spacing={2}>
            <Icon as={FiUsers} color={mutedColor} boxSize={8} />
            <Text color={mutedColor} fontSize="sm">Sin retiros anticipados registrados</Text>
          </VStack>
        ) : (
          <TableContainer overflowX="auto">
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr bg="cream.bg2" borderBottom="1px solid" borderColor="cream.line">
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Alumno</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em" display={{ base: 'none', md: 'table-cell' }}>Lector</Th>
                  <Th py={3} fontSize="11px" fontWeight="800" color="ink.400" textTransform="uppercase" letterSpacing=".05em">Fecha y hora</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {logs.map(log => (
                  <Tr key={log.id}
                    _hover={{ bg: 'coral.tint' }} transition="background .13s"
                    borderTop="1px solid" borderColor="cream.line"
                  >
                    <Td py={3} maxW="180px">
                      <Text fontWeight="700" fontSize="14px" color="ink.900" noOfLines={1}>
                        {namesByRut[log.rut] ?? log.rut}
                      </Text>
                      <Text fontSize="11.5px" color="ink.400" fontWeight="500">RUT {log.rut}</Text>
                    </Td>
                    <Td py={3} fontSize="13.5px" color="ink.700" fontWeight="500" display={{ base: 'none', md: 'table-cell' }}>
                      <HStack spacing="4px">
                        <Icon as={FiMapPin} boxSize="12px" color="ink.400" />
                        <Text noOfLines={1}>{log.scanner_id ?? '—'}</Text>
                      </HStack>
                    </Td>
                    <Td py={3} fontSize="13px" color="ink.400">
                      {formatDateTime(log.scanned_at)}
                    </Td>
                    <Td py={3} isNumeric>
                      {log.survey_response_id ? (
                        <Tooltip label="Ver ficha">
                          <IconButton
                            icon={<FiFileText />} w="32px" h="32px" minW="32px" borderRadius="9px"
                            bg="butter.bg" color="butter.ink" border="none"
                            _hover={{ bg: '#FCE7CC' }}
                            onClick={() => handleOpenFicha(log.survey_response_id)}
                            aria-label="Ver ficha"
                          />
                        </Tooltip>
                      ) : (
                        <Text fontSize="12.5px" color="ink.400">—</Text>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <ModalSurveyResponse
        isOpen={isFichaOpen}
        onClose={() => setIsFichaOpen(false)}
        customer={{ first_name: fichaData?.survey?.name ?? '', last_name: '', username: '' }}
        data={fichaData}
      />
    </Box>
  );
};

export default PickupExitsTab;
